import accessConfig from "../config/accessConfig";
import { useSelector } from "react-redux";
import { selectRoles } from "../features/auth/authSlice";

const USER_ROLES = [];

const hasRequiredRoles = (
  requiredRoles,
  userRoles = USER_ROLES,
  organisation
) => {
  if (requiredRoles == null) return true;
  if (!Array.isArray(userRoles) || !Array.isArray(requiredRoles)) return false;
  return userRoles.some((userRole) => {
    const [roleInOrganisation, usersOrganisation] = userRole.split("@");
    const hasRole = requiredRoles.indexOf(roleInOrganisation) > -1;
    if (usersOrganisation == null) return hasRole;
    return hasRole && organisation === usersOrganisation;
  });
};

const collectUserOrganisations = (userRoles) => {
  const orgs = [];
  userRoles.forEach((userRole) => {
    const userOrganisation = userRole.split("@")[1];
    if (userOrganisation != null) orgs.push(userOrganisation);
  });
  return orgs;
};

const hasAccessToLink = (link, userRoles = USER_ROLES) => {
  if (typeof link !== "string" || link === "") return false;
  if (link.includes("://")) return true;
  if (link.startsWith("?")) return true;

  // Match on the path only — a query string or hash (e.g. the participant id in
  // `/org/test?participant=123`) would otherwise stay glued to the last segment
  // and never match a route key, hiding the link.
  const path = link.split(/[?#]/)[0];

  const userOrganisations = collectUserOrganisations(userRoles);

  let organisation;
  const meaningfulSegments = path
    .split("/")
    .filter(Boolean)
    .filter((segment) => {
      if (userOrganisations.indexOf(segment) < 0) return true;
      organisation = segment;
      return false;
    });

  if (meaningfulSegments.length === 0) return false;

  const fullPath = meaningfulSegments.join("/");
  const exact = accessConfig.routes[fullPath];
  if (exact != null) {
    return hasRequiredRoles(exact, userRoles, organisation);
  }

  const fallback = accessConfig.routes[meaningfulSegments[0]];
  if (fallback != null) {
    return hasRequiredRoles(fallback, userRoles, organisation);
  }

  return false;
};

const hasAccessToApi = (apiCall, userRoles, organisation) => {
  if (accessConfig.apiCalls[apiCall] == null) {
    console.error(apiCall, "not found in accessConfig");
    return false;
  }
  return hasRequiredRoles(
    accessConfig.apiCalls[apiCall],
    userRoles,
    organisation
  );
};

const useHasAccess = ({ link, apiCall, organisation }) => {
  const roles = useSelector(selectRoles);
  if (roles == null) return null;
  if (link != null) return hasAccessToLink(link, roles);
  if (apiCall != null) return hasAccessToApi(apiCall, roles, organisation);
  return true;
};

const useHasAccessFunctions = () => {
  const roles = useSelector(selectRoles);
  return {
    hasAccessToLink: (link) => {
      return hasAccessToLink(link, roles);
    },
  };
};

export default useHasAccess;
export { useHasAccessFunctions };
